/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRecentRequests, RequestEntry } from "@/api/requests";

const EMPTY: RequestEntry[] = [];
const POLL_MS = 1500;

/**
 * Polls the proxied-request log and shows it newest first, de-duped and
 * capped. New requests arrive in batches (one poll = a handful at once), so
 * they are queued and released one by one across the poll interval; combined
 * with the rows' own entrance animation the feed flows instead of jumping.
 * `baseline` is the newest id of the first load: rows above it are "fresh".
 */
export function useLiveRequests(maxRows = 60) {
  const lastId = useRef(0);
  const pending = useRef<RequestEntry[]>([]); // oldest first
  const first = useRef(true);
  const [shown, setShown] = useState<RequestEntry[]>(EMPTY);
  const [baseline, setBaseline] = useState(Number.MAX_SAFE_INTEGER);
  const maxRef = useRef(maxRows);
  useEffect(() => {
    maxRef.current = maxRows;
  }, [maxRows]);

  const { data, isError } = useQuery({
    queryKey: ["live-requests", maxRows],
    queryFn: async () => {
      const res = await getRecentRequests(lastId.current, Math.min(maxRows, 500));
      if (res.enabled && res.entries.length > 0) {
        lastId.current = res.lastId;
        if (first.current) {
          // the backlog is shown at once; only what arrives later is animated
          first.current = false;
          setBaseline(res.lastId);
          setShown([...res.entries].reverse().slice(0, maxRows));
        } else {
          pending.current.push(...res.entries);
        }
      } else if (first.current && res.enabled) {
        first.current = false;
        setBaseline(res.lastId);
      }
      return { enabled: res.enabled };
    },
    refetchInterval: POLL_MS,
    retry: false,
  });

  // Release queued requests one at a time, fast enough to be done before the next poll.
  useEffect(() => {
    let timer = 0;
    const tick = () => {
      // a busy proxy sends dozens of requests a second: never faster than ~6 beats a
      // second, releasing a few at a time when the queue is long, so the feed stays readable
      const batch = Math.max(1, Math.ceil(pending.current.length / 8));
      const next = pending.current.splice(0, batch);
      if (next.length > 0) {
        setShown((cur) => {
          const have = new Set(cur.map((r) => r.id));
          const fresh = next.filter((r) => !have.has(r.id)).reverse();
          return fresh.length ? [...fresh, ...cur].slice(0, maxRef.current) : cur;
        });
      }
      timer = window.setTimeout(tick, next.length > 0 ? 160 : 80);
    };
    timer = window.setTimeout(tick, 80);
    return () => window.clearTimeout(timer);
  }, []);

  const clear = useCallback(() => {
    pending.current = [];
    setShown(EMPTY);
  }, []);

  return { enabled: data?.enabled ?? true, rows: shown, isError, clear, baseline };
}

export function requestStatusTone(status: number): string {
  if (status >= 500) return "text-red-500 border-red-500/40 bg-red-500/10";
  if (status >= 400) return "text-amber-500 border-amber-500/40 bg-amber-500/10";
  if (status >= 300) return "text-sky-500 border-sky-500/40 bg-sky-500/10";
  if (status >= 200) return "text-emerald-500 border-emerald-500/40 bg-emerald-500/10";
  return "text-gray-400 border-gray-400/40 bg-gray-400/10";
}

export function requestAgo(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (s < 5) return "now";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}
